// ============================================================================
// BOQ — autosave (automatic restore-points, not document save)
// ============================================================================
// The live bill is always persisted; this layer captures automatic snapshot
// revisions so you can roll back:
//   - beforeBulk(label): snapshot just before a risky op (import / paste /
//     bulk delete).
//   - markDirty(): note that the bill changed; a throttled timer takes at most
//     one snapshot per IDLE_MS of active editing.
// Auto revisions live under the project's ACTIVE stage (the milestone the live
// bill currently belongs to; falls back to highest-seq, and one Estimate is
// created if none exists), and are pruned to KEEP_PER_MILESTONE per milestone.
// ============================================================================

import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';

const IDLE_MS = 10 * 60 * 1000;     // throttle: at most one auto-snapshot / 10 min
const CHECK_MS = 60 * 1000;          // timer tick
const KEEP_PER_MILESTONE = 10;       // retained auto revisions per milestone

export const useBoqAutosave = (boqProjectId) => {
  const dirtyRef = useRef(false);
  const lastAutoRef = useRef(0);
  const inFlightRef = useRef(false);

  const ensureMilestone = useCallback(async () => {
    // Prefer the active stage so autosaves land under the stage actually being
    // edited (not whichever milestone happens to have the highest seq).
    const { data: proj } = await supabase.schema('boq').from('projects')
      .select('active_milestone_id').eq('id', boqProjectId).maybeSingle();
    if (proj?.active_milestone_id) return proj.active_milestone_id;
    const { data, error } = await supabase.schema('boq').from('bill_milestones')
      .select('id, seq').eq('project_id', boqProjectId).order('seq', { ascending: false }).limit(1);
    if (error) throw error;
    if (data && data.length) {
      // Adopt the existing highest-seq milestone as the active stage.
      await supabase.schema('boq').from('projects').update({ active_milestone_id: data[0].id }).eq('id', boqProjectId);
      return data[0].id;
    }
    const { data: m, error: insErr } = await supabase.schema('boq').from('bill_milestones')
      .insert({ project_id: boqProjectId, name: 'Estimate', kind: 'estimate', seq: 1 })
      .select('id').single();
    if (insErr) throw insErr;
    await supabase.schema('boq').from('projects').update({ active_milestone_id: m.id }).eq('id', boqProjectId);
    return m.id;
  }, [boqProjectId]);

  const prune = useCallback(async (milestoneId) => {
    const { data, error } = await supabase.schema('boq').from('bill_versions')
      .select('id').eq('milestone_id', milestoneId).eq('is_auto', true)
      .order('snapshot_at', { ascending: false });
    if (error) return;
    const old = (data || []).slice(KEEP_PER_MILESTONE).map((r) => r.id);
    if (old.length) await supabase.schema('boq').from('bill_versions').delete().in('id', old);
  }, []);

  const autoSnapshot = useCallback(async (label) => {
    if (!boqProjectId || inFlightRef.current) return { success: false };
    inFlightRef.current = true;
    try {
      const mid = await ensureMilestone();
      const { error: rpcErr } = await supabase.schema('boq')
        .rpc('snapshot_bill_version', { p_milestone_id: mid, p_label: label || 'Auto', p_is_auto: true });
      if (rpcErr) throw rpcErr;
      await prune(mid);
      lastAutoRef.current = Date.now();
      dirtyRef.current = false;
      return { success: true };
    } catch (err) {
      console.error('[useBoqAutosave] autoSnapshot failed', err);
      return { success: false, error: err.message };
    } finally {
      inFlightRef.current = false;
    }
  }, [boqProjectId, ensureMilestone, prune]);

  // Snapshot before a bulk/destructive op (awaited by the caller).
  const beforeBulk = useCallback((label) => autoSnapshot(label), [autoSnapshot]);

  const markDirty = useCallback(() => { dirtyRef.current = true; }, []);

  // Throttled session autosave.
  useEffect(() => {
    if (!boqProjectId) return undefined;
    const id = setInterval(() => {
      if (dirtyRef.current && Date.now() - lastAutoRef.current > IDLE_MS) {
        autoSnapshot('Auto — periodic');
      }
    }, CHECK_MS);
    return () => clearInterval(id);
  }, [boqProjectId, autoSnapshot]);

  return { beforeBulk, markDirty };
};
